export type TipoItinerario = "tour" | "restaurante" | "caballeriza" | "bar" | "cabana";
export type EstadoItinerario = "borrador" | "solicitada" | "confirmada";
export type DatosVisita = { nombre: string; telefono: string; correo: string };
export type ItinerarioItem = {
  id: string;
  lugar: string;
  tipo: TipoItinerario;
  fecha: string;
  hora: string;
  personas: number;
  duracion: number;
  detalle: string;
  continuarEn?: string;
  estado?: EstadoItinerario;
  /** Folio de la solicitud real (BAR-… o CAB-…) cuando la actividad ya fue solicitada. */
  folio?: string;
};

export const CLAVE_ITINERARIO = "sierra-tours-itinerario-v1";
export const CLAVE_DATOS_VISITA = "sierra-tours-datos-visita-v1";
export const EVENTO_ITINERARIO = "sierra-tours-itinerario";
export const EVENTO_ACTIVIDAD_AGREGADA = "sierra-tours-actividad-agregada";
export const EVENTO_SYNC = "sierra-tours-sync";
const CLAVE_TOKEN = "sierra-tours-visita-token-v1";
export type EstadoSync = "local" | "guardando" | "guardada" | "error";

import { aCarga, fusionar, tokenValido } from "./itinerario-sync";

const disponible = () => typeof window !== "undefined";
const avisar = () => { if (disponible()) window.dispatchEvent(new Event(EVENTO_ITINERARIO)); };

const barOculto = () => disponible() && document.documentElement.hasAttribute("data-bar-reservas-oculto");

export function leerItinerario(): ItinerarioItem[] {
  if (!disponible()) return [];
  try {
    const datos: unknown = JSON.parse(window.sessionStorage.getItem(CLAVE_ITINERARIO) ?? "[]");
    return Array.isArray(datos) ? (datos as ItinerarioItem[]).filter((i) => !(i.tipo === "bar" && barOculto())) : [];
  } catch { return []; }
}

function escribirLocal(items: ItinerarioItem[]) {
  if (!disponible()) return;
  try { window.sessionStorage.setItem(CLAVE_ITINERARIO, JSON.stringify(ordenarItinerario(items))); } catch { /* El plan sigue visible durante esta visita. */ }
  avisar();
}

export function guardarItinerario(items: ItinerarioItem[]) {
  escribirLocal(items);
  programarSync();
}

// ───────── Servidor: la visita se conserva con un token que solo guarda este navegador (o un enlace que el visitante copia).
let estadoSync: EstadoSync = "local", temporizador: ReturnType<typeof setTimeout> | undefined;
export const leerEstadoSync = () => estadoSync;
const cambiarEstado = (e: EstadoSync) => { estadoSync = e; if (disponible()) window.dispatchEvent(new Event(EVENTO_SYNC)); };
const hayServidor = () => disponible() && process.env.NEXT_PUBLIC_EXPORT !== "1";
const leerToken = (): { token: string; version: number } | null => {
  try { const d = JSON.parse(window.localStorage.getItem(CLAVE_TOKEN) ?? "null"); return d && tokenValido(d.token) && Number.isInteger(d.version) ? d : null; } catch { return null; }
};
const guardarToken = (d: { token: string; version: number } | null) => { try { if (d) window.localStorage.setItem(CLAVE_TOKEN, JSON.stringify(d)); else window.localStorage.removeItem(CLAVE_TOKEN); } catch { /* sin almacenamiento */ } };
async function pedir(metodo: "GET" | "POST" | "PUT", cuerpo?: object, token?: string) {
  const r = await fetch("/api/itinerario", { method: metodo, headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) }, body: cuerpo ? JSON.stringify(cuerpo) : undefined, cache: "no-store" });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.mensaje ?? "No se pudo guardar tu visita"), { estado: r.status });
  return d;
}

export function programarSync() {
  if (!hayServidor()) return;
  clearTimeout(temporizador);
  temporizador = setTimeout(() => { void sincronizar(); }, 800);
}

/** Envía la visita al servidor. Un fallo no borra nada local y se muestra como «error», no como guardada. */
export async function sincronizar(reintento = 0): Promise<void> {
  if (!hayServidor()) return;
  const items = leerItinerario(), guardado = leerToken();
  if (!items.length && !guardado) { cambiarEstado("local"); return; }
  cambiarEstado("guardando");
  try {
    const carga = aCarga(items, leerDatosVisita());
    if (!guardado) { const r = await pedir("POST", carga); guardarToken({ token: r.token, version: r.version }); }
    else {
      try { const r = await pedir("PUT", { ...carga, version: guardado.version }, guardado.token); guardarToken({ token: guardado.token, version: r.version }); }
      catch (e) {
        const estado = (e as { estado?: number }).estado;
        if (reintento < 2 && estado === 409) { const s = await pedir("GET", undefined, guardado.token); escribirLocal(fusionar(items, s.datos.items)); guardarToken({ token: guardado.token, version: s.version }); return sincronizar(reintento + 1); }
        if (reintento < 2 && estado === 404) { guardarToken(null); return sincronizar(reintento + 1); }
        throw e;
      }
    }
    cambiarEstado("guardada");
  } catch { cambiarEstado("error"); }
}

/** Recupera la visita guardada (token de este navegador o el de un enlace). Devuelve si encontró algo. */
export async function recuperarVisita(tokenDeEnlace?: string | null): Promise<boolean> {
  if (!hayServidor()) return false;
  if (tokenDeEnlace && tokenValido(tokenDeEnlace)) guardarToken({ token: tokenDeEnlace, version: 0 });
  const guardado = leerToken(); if (!guardado) return false;
  try {
    const s = await pedir("GET", undefined, guardado.token);
    guardarToken({ token: guardado.token, version: s.version });
    escribirLocal(fusionar(leerItinerario(), s.datos.items));
    const v = s.datos.visitante; if (v && !leerDatosVisita()) guardarDatosVisita({ nombre: v.nombre ?? "", telefono: v.telefono ?? "", correo: "" });
    cambiarEstado("guardada"); return true;
  } catch (e) { if ((e as { estado?: number }).estado === 404) guardarToken(null); else cambiarEstado("error"); return false; }
}
export const enlaceVisita = (idioma: string) => { const t = disponible() ? leerToken() : null; return t ? `${window.location.origin}/${idioma}/itinerario?v=${t.token}` : null; };

export const ordenarItinerario = (items: ItinerarioItem[]) => [...items].sort((a, b) => `${a.fecha}${a.hora}`.localeCompare(`${b.fecha}${b.hora}`));

export function agregarAlItinerario(item: ItinerarioItem) {
  const anteriores = leerItinerario().filter((x) => x.id !== item.id);
  guardarItinerario([...anteriores, { ...item, estado: item.estado ?? "borrador" }]);
  if (disponible()) window.dispatchEvent(new CustomEvent(EVENTO_ACTIVIDAD_AGREGADA, { detail: item }));
}

export function leerDatosVisita(): DatosVisita | null {
  if (!disponible()) return null;
  try {
    const datos: unknown = JSON.parse(window.sessionStorage.getItem(CLAVE_DATOS_VISITA) ?? "null");
    if (!datos || typeof datos !== "object") return null;
    const d = datos as Partial<DatosVisita>;
    return typeof d.nombre === "string" && typeof d.telefono === "string" && typeof d.correo === "string" ? { nombre: d.nombre, telefono: d.telefono, correo: d.correo } : null;
  } catch { return null; }
}

export function guardarDatosVisita(datos: DatosVisita) {
  if (!disponible()) return;
  try { window.sessionStorage.setItem(CLAVE_DATOS_VISITA, JSON.stringify(datos)); } catch { /* El navegador puede bloquear el almacenamiento privado. */ }
  avisar();
  programarSync();
}
